import React from "react";
import ReplyIcon from "@material-ui/icons/Reply";
import WhatsAppIcon from "@material-ui/icons/WhatsApp";
import ThumbUpIcon from "@material-ui/icons/ThumbUp";
import MoreHorizSharpIcon from "@material-ui/icons/MoreHorizSharp";
import LanguageOutlinedIcon from "@material-ui/icons/LanguageOutlined";
import { ImageOutlined } from "@material-ui/icons";
import {
  withStyles,
  Typography,
  Card,
  CardMedia,
  CardContent,
  Avatar,
  Box,
  Button,
  Grid,
  Tooltip,
} from "@material-ui/core";
import { replaceDomainInData } from "../../../helpers/mediaHelper";
import JsxCarousel from "./JsxCarousel";

// Carousel preview card is 300px wide and its media tile is square, so this
// doubles as both the media height and the empty-slot placeholder height —
// every slide occupies the same box whether or not it has media yet.
const CAROUSEL_MEDIA_SIZE = 300;

const COPY_LIMITS = {
  primaryText: 2200,
  headline: 40,
  headlineDescription: 30,
};

const roomLeft = (element, field) => {
  const limit = COPY_LIMITS[field];
  if (!limit) return Infinity;
  const selection = window.getSelection();
  const selected =
    selection && !selection.isCollapsed ? String(selection).length : 0;
  return limit - (element.innerText.length - selected);
};

export function AdView(props) {
  const {
    classes,
    payload,
    maxHeight,
    ctaButton: ctaFromProps,
    websiteUrl,
    activeCardIndex,
    onActiveCardChange,
  } = props;
  const {
    pageDetails,
    previewLink,
    adCaption,
    adHeadline,
    adHeadlineDescription,
    mediaType,
    imageFile,
    videoFile,
    thumbnailUrl,
    ctaButton,
    adType,
    adFormat,
    cards = [],
  } = payload || {};
  const { picture, pageName, whatsappNumber } = pageDetails || {};

  const isCarousel = adFormat === "carousel" && Array.isArray(cards) && cards.length > 0;

  const caption = adCaption || " Enter Caption";
  const headline = adHeadline || `Contact ${pageName}` || "Enter Headline";
  const cta = ctaFromProps || ctaButton;

  const {
    onCopyChange,
    onCtaClick,
    captionClamp,
    hidePageHeader,
    hideDestination,
    footerBg,
  } = props;

  const captionRef = React.useRef(null);
  const [captionExpanded, setCaptionExpanded] = React.useState(false);
  const [captionOverflows, setCaptionOverflows] = React.useState(false);

  React.useEffect(() => {
    const el = captionRef.current;
    if (!el || !captionClamp || captionExpanded) return undefined;
    const measure = () =>
      setCaptionOverflows(el.scrollHeight - el.clientHeight > 1);
    measure();
    window.addEventListener("resize", measure);
    return () => window.removeEventListener("resize", measure);
  }, [caption, captionClamp, captionExpanded]);


  const shownCopy = {
    primaryText: caption,
    headline,
    headlineDescription: adHeadlineDescription || "Add description",
  };

  const editableProps = (field) =>
    onCopyChange
      ? {
        contentEditable: true,
        suppressContentEditableWarning: true,
        spellCheck: false,
        className: classes.editableText,
        onClick: (event) => {
          event.preventDefault();
          event.stopPropagation();
        },
        onFocus: () => {
          if (field === "primaryText") setCaptionExpanded(true);
        },
        // contentEditable has no maxlength, so the cap is enforced at the two
        // points text can enter: typing and pasting.
        onBeforeInput: (event) => {
          const incoming = (event.data || "").length;
          if (incoming && incoming > roomLeft(event.currentTarget, field)) {
            event.preventDefault();
          }
        },
        onPaste: (event) => {
          event.preventDefault();
          const room = roomLeft(event.currentTarget, field);
          if (room <= 0) return;
          const pasted = (
            event.clipboardData || window.clipboardData
          ).getData("text");
          document.execCommand("insertText", false, pasted.slice(0, room));
        },
        onBlur: (event) => {
          const limit = COPY_LIMITS[field] || Infinity;
          const next = event.currentTarget.innerText.trim().slice(0, limit);
          if (next === String(shownCopy[field] || "").trim()) return;
          onCopyChange(field, next);
        },
      }
      : null;

  const clampStyle =
    captionClamp && !captionExpanded
      ? {
        display: "-webkit-box",
        WebkitBoxOrient: "vertical",
        WebkitLineClamp: captionClamp,
        overflow: "hidden",
      }
      : null;

  const isValidUrl = (url) => {
    if (!url || typeof url !== "string") return false;
    const trimmedUrl = url.trim();
    return (
      trimmedUrl !== "" &&
      trimmedUrl !== "@media" &&
      trimmedUrl !== "media" &&
      (trimmedUrl.startsWith("http://") ||
        trimmedUrl.startsWith("https://") ||
        trimmedUrl.startsWith("/") ||
        trimmedUrl.startsWith("data:"))
    );
  };

  const imageUrl = imageFile?.publicUrl ? replaceDomainInData(imageFile.publicUrl) : null;
  const videoUrl = videoFile?.publicUrl ? replaceDomainInData(videoFile.publicUrl) : null;
  const hasValidImage = imageFile && isValidUrl(imageUrl);
  const hasValidVideo = videoFile && isValidUrl(videoUrl);

  const ctaLabelMap = {
    APPLY_NOW: "Apply now",
    BOOK_NOW: "Book now",
    CONTACT_US: "Contact us",
    DOWNLOAD: "Download",
    LEARN_MORE: "Learn more",
    REQUEST_TIME: "Request time",
    SEE_MENU: "See menu",
    SHOP_NOW: "Shop now",
    SIGN_UP: "Sign up",
    WATCH_MORE: "Watch more",
    DONATE_NOW: "Donate now",
  };

  const resolvedCta =
    ctaLabelMap[cta] ||
    (cta
      ? cta
        .toString()
        .replace(/_/g, " ")
        .toLowerCase()
        .replace(/^\w|\s\w/g, (m) => m.toUpperCase())
      : "Visit website");

  // The media + footer unit is shared between single format (rendered once)
  // and carousel format (once per card, as a JsxCarousel slide) — same
  // layout either way, just fed different data. Card-mode callers pass
  // already-`replaceDomainInData`d URLs and their own headline/description/
  // websiteUrl; CTA stays ad-level (`resolvedCta`) and is reused unchanged
  // across every slide, since `cards[]` has no per-card CTA field.
  const renderCardBlock = ({
    cardMediaType,
    cardImageUrl,
    cardVideoUrl,
    cardThumbnailUrl,
    cardHeadline,
    cardDescription,
    cardWebsiteUrl,
    mediaTitle,
  }) => {
    const validImage = isValidUrl(cardImageUrl);
    const validVideo = isValidUrl(cardVideoUrl);
    // The fixed-height crop (like Facebook's actual carousel card) only
    // applies in carousel mode — single-format media keeps its original
    // maxHeight/contain behavior, unchanged.
    const mediaClassName = isCarousel ? classes.carouselMedia : classes.media;
    return (
      <Box style={{ width: "100%", minWidth: 0 }}>
        {validImage || validVideo ? (
          cardMediaType === "VIDEO" && validVideo ? (
            <CardMedia
              className={mediaClassName}
              src={cardVideoUrl}
              alt="media"
              title={mediaTitle}
              component={"video"}
              controls
              poster={replaceDomainInData(cardThumbnailUrl)}
            />
          ) : validImage ? (
            <CardMedia
              className={mediaClassName}
              src={cardImageUrl}
              alt="media"
              title={mediaTitle}
              component={"img"}
            />
          ) : null
        ) : (
          <Box
            height={isCarousel ? `${CAROUSEL_MEDIA_SIZE}px` : "250px"}
            width="100%"
            bgcolor="#fafafa"
            style={{ display: "flex", justifyContent: "center" }}
          >
            <Grid
              style={{
                display: "flex",
                flexDirection: "column",
                justifyContent: "center",
              }}
            >
              <ImageOutlined
                style={{
                  width: "100px",
                  height: "100px",
                  color: "rgb(208 212 208)",
                }}
              />
            </Grid>
          </Box>
        )}

        <Box
          bgcolor={footerBg || "#fafafa"}
          style={{ display: "flex", paddingTop: hideDestination ? 10 : 0 }}
        >
          <Box
            style={{
              display: "flex",
              width: "60%",
              minWidth: 0,
              justifyContent: "space-between",
            }}
          >
            <Box style={{ width: "100%", minWidth: 0 }}>
              {hideDestination ? null : (
                <Typography
                  variant="h5"
                  style={{
                    margin: "10px",
                    marginBottom: "0px",
                    fontSize: "12px",
                  }}
                  gutterBottom
                >
                  {["website_traffic", "website_to_whatsapp"].includes(
                    adType?.value
                  )
                    ? cardWebsiteUrl
                      ? cardWebsiteUrl.replace(/^https?:\/\//, "")
                      : "WEBSITE"
                    : adType?.value === "lead_form"
                      ? "LEAD FORM"
                      : "WHATSAPP"}
                </Typography>
              )}
              <Typography
                variant="h5"
                style={{
                  marginLeft: "10px",
                  fontSize: "14px",
                  fontWeight: "bold",
                  color: `${cardHeadline ? "black" : "rgb(211 214 211)"}`,
                  whiteSpace: "nowrap",
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                }}
              >
                {cardHeadline}
              </Typography>
              {cardDescription && (
                <Typography
                  variant="body2"
                  style={{
                    marginLeft: "10px",
                    fontSize: "12px",
                    color: "rgb(120,120,120)",
                    whiteSpace: "nowrap",
                    overflow: "hidden",
                    textOverflow: "ellipsis",
                  }}
                >
                  {cardDescription}
                </Typography>
              )}
              <Box marginBottom={0.5}></Box>
            </Box>
          </Box>
          <Tooltip title={onCtaClick ? "Click to change button" : ""}>
            <Box
              onClick={
                onCtaClick
                  ? (event) => {
                    event.preventDefault();
                    event.stopPropagation();
                    onCtaClick(event);
                  }
                  : undefined
              }
              className={onCtaClick ? classes.editableCta : undefined}
              style={{ display: "flex", alignItems: "center", width: "40%" }}
            >
            {["website_traffic", "website_to_whatsapp"].includes(
              adType?.value
            ) ? (
              <Tooltip
                title={onCtaClick ? "" : cardWebsiteUrl || "No URL added"}
                arrow={!onCtaClick}
              >
                <span style={{ display: "inline-flex" }}>
                  <Button
                    variant="outlined"
                    style={{
                      color: cardWebsiteUrl ? "#darkgreen" : "rgba(0, 0, 0, 0.26)",
                      borderColor: cardWebsiteUrl ? "darkgreen" : "rgba(0, 0, 0, 0.26)",
                      cursor: cardWebsiteUrl ? "pointer" : "not-allowed",
                      marginLeft: "30px",
                      whiteSpace: "nowrap",
                    }}
                    href={cardWebsiteUrl || undefined}
                    target="_blank"
                    rel="noopener noreferrer"
                    size="small"
                    disabled={!cardWebsiteUrl}
                    component={cardWebsiteUrl ? "a" : "button"}
                  >
                    {resolvedCta}
                  </Button>
                </span>
              </Tooltip>
            ) : adType?.value === "lead_form" ? (
              <Button
                variant="outlined"
                style={{
                  color: "#darkgreen",
                  borderColor: "darkgreen",
                  marginLeft: "30px",
                }}
                size="small"
              >
                {resolvedCta}
              </Button>
            ) : (
              <Button
                variant="outlined"
                style={{
                  color: "#darkgreen",
                  borderColor: "darkgreen",
                }}
                size="small"
                startIcon={<WhatsAppIcon />}
              >
                Whatsapp
              </Button>
            )}
            </Box>
          </Tooltip>
        </Box>
      </Box>
    );
  };

  // WhatsApp Status ads render as a full-screen vertical Status/Story
  // surface (Meta requires the matching Instagram Story placement), not
  // the feed-card layout below, so this is handled as an early return.
  if (adType?.value === "whatsapp_status") {
    return (
      <a
        href={previewLink}
        target="_blank"
        rel="noopener noreferrer"
        style={{
          cursor: previewLink ? "pointer" : "default",
          width: "fit-content",
          display: "inline-block",
          // Center over the preview column (parent is display: grid), which
          // is sized by the wider "Estimated reach" card below.
          justifySelf: "center",
        }}
      >
        <Tooltip
          title={
            previewLink ? "Click to see how people will experience your ad" : ""
          }
        >
          <Box className={classes.statusCard}>
            {hasValidImage || hasValidVideo ? (
              mediaType === "VIDEO" && hasValidVideo ? (
                <video
                  src={videoUrl}
                  poster={replaceDomainInData(thumbnailUrl)}
                  className={classes.statusMedia}
                  autoPlay
                  muted
                  loop
                  playsInline
                />
              ) : hasValidImage ? (
                <img src={imageUrl} alt="media" className={classes.statusMedia} />
              ) : null
            ) : (
              <Box
                className={classes.statusMedia}
                style={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  backgroundColor: "#111",
                }}
              >
                <ImageOutlined
                  style={{ width: "80px", height: "80px", color: "rgba(255,255,255,0.3)" }}
                />
              </Box>
            )}

            {/* Readability gradients, like a real Status/Story viewer */}
            <Box
              style={{
                position: "absolute",
                top: 0,
                left: 0,
                right: 0,
                height: "35%",
                background:
                  "linear-gradient(180deg, rgba(0,0,0,0.55) 0%, rgba(0,0,0,0) 100%)",
                pointerEvents: "none",
              }}
            />
            <Box
              style={{
                position: "absolute",
                bottom: 0,
                left: 0,
                right: 0,
                height: "30%",
                background:
                  "linear-gradient(0deg, rgba(0,0,0,0.65) 0%, rgba(0,0,0,0) 100%)",
                pointerEvents: "none",
              }}
            />

            {/* Progress segments bar */}
            <Box
              style={{
                position: "absolute",
                top: "8px",
                left: "8px",
                right: "8px",
                display: "flex",
                gap: "4px",
              }}
            >
              {[0, 1, 2].map((segmentIndex) => (
                <Box
                  key={segmentIndex}
                  style={{
                    flex: 1,
                    height: "3px",
                    borderRadius: "3px",
                    backgroundColor:
                      segmentIndex === 0 ? "#fff" : "rgba(255,255,255,0.35)",
                  }}
                />
              ))}
            </Box>

            {/* Advertiser row */}
            <Box
              style={{
                position: "absolute",
                top: "16px",
                left: "8px",
                right: "8px",
                display: "flex",
                alignItems: "center",
              }}
            >
              <Avatar
                src={replaceDomainInData(picture)}
                style={{
                  width: "28px",
                  height: "28px",
                  marginRight: "8px",
                  border: "1px solid rgba(255,255,255,0.6)",
                }}
              />
              <Box>
                <Typography
                  style={{
                    color: "#fff",
                    fontSize: "12px",
                    fontWeight: 600,
                    lineHeight: 1.2,
                  }}
                >
                  {pageName || "Page Name"}
                </Typography>
                <Typography
                  style={{
                    color: "rgba(255,255,255,0.75)",
                    fontSize: "10px",
                    lineHeight: 1.2,
                  }}
                >
                  Sponsored
                </Typography>
              </Box>
            </Box>

            {adCaption && (
              <Typography
                style={{
                  position: "absolute",
                  bottom: "56px",
                  left: "12px",
                  right: "12px",
                  color: "#fff",
                  fontSize: "12px",
                  whiteSpace: "pre-line",
                  wordBreak: "break-word",
                  textShadow: "0px 1px 2px rgba(0,0,0,0.6)",
                }}
              >
                {caption}
              </Typography>
            )}

            {/* Bottom CTA chip */}
            <Box
              style={{
                position: "absolute",
                bottom: "14px",
                left: "12px",
                right: "12px",
                display: "flex",
                justifyContent: "center",
              }}
            >
              <Button
                variant="contained"
                size="small"
                startIcon={<WhatsAppIcon style={{ fontSize: "16px" }} />}
                style={{
                  backgroundColor: "#fff",
                  color: "#075E54",
                  borderRadius: "20px",
                  textTransform: "none",
                  fontWeight: 600,
                  fontSize: "12px",
                  boxShadow: "0px 2px 4px rgba(0,0,0,0.3)",
                }}
              >
                Chat on WhatsApp
              </Button>
            </Box>
          </Box>
        </Tooltip>
      </a>
    );
  }

  return (
    <Box style={{ width: "fit-content" }}>
    <a
      href={previewLink}
      target="_blank"
      rel="noopener noreferrer"
      style={{
        cursor: previewLink ? "pointer" : "default",
        width: "fit-content",
        display: "inline-block",
      }}
    >
      <Tooltip
        title={
          previewLink ? "Click to see how people will experience your ad" : ""
        }
      >
        <Card
          className={classes.root}
          style={{ width: "300px", overflow: "hidden" }}
        >
          {!hidePageHeader ? (
            <Box
              style={{
                display: "flex",
                width: "100%",
                justifyContent: "space-between",
              }}
            >
              <Box
                style={{
                  display: "flex",
                  alignItems: "center",
                  margin: "15px 15px 0",
                }}
              >
                <Box style={{ marginRight: "10px" }}>
                  <Avatar src={replaceDomainInData(picture)} />
                </Box>
                <Box>
                  <Typography variant="h5">{pageName || "Page Name"}</Typography>
                  <Box style={{ display: "flex", alignItems: "center" }}>
                    <Typography
                      variant="body2"
                      color="textSecondary"
                      style={{ marginRight: "0.2em" }}
                    >
                      sponsored
                    </Typography>
                    <LanguageOutlinedIcon
                      style={{ fontSize: "15px" }}
                      color="textSecondary"
                    />
                  </Box>
                </Box>
              </Box>
              {!props.isFromFlowKeyword && (
                <MoreHorizSharpIcon
                  style={{
                    margin: "15px 15px 0",
                    fontSize: "18px",
                    display: "flex",
                  }}
                  color="textSecondary"
                />
              )}
            </Box>
          ) : null}

          <Box>
            <CardContent
              style={
                maxHeight
                  ? {
                    maxHeight,
                    overflowX: "auto",
                    padding: "16px",
                    paddingBottom: "8px",
                  }
                  : {
                    padding: "16px",
                    paddingBottom: "8px",
                  }
              }
            >
              <Tooltip title={onCopyChange ? "Click to edit primary text" : ""}>
                <Typography
                  ref={captionRef}
                  variant="h6"
                  align="left"
                  {...editableProps("primaryText")}
                  style={{
                    color: `${adCaption ? "black" : "rgb(178 182 178)"}`,
                    whiteSpace: "pre-line",
                    wordBreak: "break-word",
                    ...clampStyle,
                  }}
                >
                  {caption}
                </Typography>
              </Tooltip>
              {captionClamp && (captionOverflows || captionExpanded) ? (
                <Typography
                  role="button"
                  tabIndex={0}
                  onClick={(event) => {
                    event.preventDefault();
                    event.stopPropagation();
                    setCaptionExpanded((open) => !open);
                  }}
                  style={{
                    display: "inline-block",
                    marginTop: "4px",
                    fontSize: "11px",
                    fontWeight: 700,
                    color: "#0a474c",
                    cursor: "pointer",
                  }}
                >
                  {captionExpanded ? "See less" : "See more"}
                </Typography>
              ) : null}
            </CardContent>

            {isCarousel ? (
              <JsxCarousel
                // Previously forced a full remount via `key={cards.length}`
                // so adding/removing a card would reopen on the right slide
                // (JsxCarousel only reads `currentSlide` at mount time) —
                // but tearing down and rebuilding every slide's DOM on every
                // add/remove was the actual cause of the 1-2s UI freeze
                // reported on those actions. `syncSlideOnCountChange` gets
                // the same "reopen on the right slide" behavior from a
                // plain state update inside JsxCarousel instead.
                syncSlideOnCountChange
                currentSlide={Math.min(
                  Math.max(activeCardIndex ?? 0, 0),
                  cards.length - 1,
                )}
                // Center the prev/next arrows on the media block, not the
                // whole card (media + headline/description/CTA footer):
                // JsxCarousel's slideContainer has a 16px top padding
                // (theme.spacing(2, 0)) before the media starts, and the
                // carousel media box is a fixed 300px tall (carouselMedia
                // class below) — so the media's vertical center sits at
                // 16 + 300/2 = 166px from the top of the carousel.
                arrowsTop="166px"
                updateCurrentSlide={onActiveCardChange}
                slides={cards.map((card, i) => {
                  const isVideoCard = card.mediaType === "video";
                  const cardImageUrl = card.imageUrl ? replaceDomainInData(card.imageUrl) : null;
                  const cardVideoUrl = card.videoUrl ? replaceDomainInData(card.videoUrl) : null;
                  return (
                    <React.Fragment key={i}>
                      {renderCardBlock({
                        cardMediaType: isVideoCard ? "VIDEO" : "IMAGE",
                        cardImageUrl,
                        cardVideoUrl,
                        // A video card's thumbnail reuses the same imageUrl
                        // field a card would otherwise use for its image
                        // (see meta-ads-carousel-backend-implementation-
                        // plan.md's wire contract) — no separate thumbnail
                        // field on the card.
                        cardThumbnailUrl: cardImageUrl,
                        cardHeadline: card.headline || "Enter Headline",
                        cardDescription: card.description,
                        cardWebsiteUrl: card.websiteUrl,
                        mediaTitle: card.headline || "Card media",
                      })}
                    </React.Fragment>
                  );
                })}
              />
            ) : (
              renderCardBlock({
                cardMediaType: mediaType,
                cardImageUrl: imageUrl,
                cardVideoUrl: videoUrl,
                cardThumbnailUrl: thumbnailUrl,
                cardHeadline: headline,
                cardDescription: adHeadlineDescription,
                cardWebsiteUrl: websiteUrl,
                mediaTitle:
                  (mediaType === "VIDEO" ? videoFile?.filename : imageFile?.filename) ||
                  "Download media",
              })
            )}


            {!props.isFromFlowKeyword && (
              <Box style={{ display: "flex", width: "100%" }}>
                <Box
                  style={{
                    display: "flex",
                    width: "100%",
                    margin: "10px",
                    marginTop: "0px",
                    justifyContent: "space-between",
                  }}
                >
                  <Box
                    style={{ display: "flex", alignItems: "center" }}
                    color="primary"
                  >
                    <ThumbUpIcon
                      style={{
                        margin: "10px",
                        marginBottom: "0px",
                        marginRight: "0.2em",
                        fontSize: "15px",
                      }}
                    />
                    <Typography
                      color="primary"
                      variant="h5"
                      style={{
                        margin: "10px",
                        marginBottom: "0px",
                        marginLeft: "0px",
                        fontSize: "13px",
                      }}
                      gutterBottom
                    >
                      Like
                    </Typography>
                  </Box>

                  <Box
                    style={{ display: "flex", alignItems: "center" }}
                    color="primary"
                  >
                    <ReplyIcon
                      style={{
                        margin: "10px",
                        marginBottom: "0px",
                        marginRight: "0.2em",
                        fontSize: "15px",
                        transform: "rotateY(180deg)",
                      }}
                    />
                    <Typography
                      color="primary"
                      variant="h5"
                      style={{
                        margin: "10px",
                        marginBottom: "0px",
                        marginLeft: "0",
                        fontSize: "13px",
                      }}
                      gutterBottom
                    >
                      Share
                    </Typography>
                  </Box>
                </Box>
              </Box>
            )}
          </Box>
        </Card>
      </Tooltip>
    </a>
    {adType?.value === "website_to_whatsapp" && !props.hideInAppBrowserNote && (
      // ADS-148: Meta shows the WhatsApp entry point only after the tap —
      // as a footer bar when the website opens in the FB/IG in-app browser
      // (mobile). Previewed here as a separate mini mock so the ad card
      // above stays true to the delivered ad unit.
      <Box style={{ marginTop: "16px", width: "300px" }}>
        <Typography
          variant="body2"
          style={{
            fontSize: "12px",
            color: "rgb(120,120,120)",
            marginBottom: "8px",
            lineHeight: 1.4,
          }}
        >
          On tap in the Facebook/Instagram mobile app, your website opens
          with a WhatsApp button (mobile only):
        </Typography>
        <Box
          style={{
            borderRadius: "4px",
            overflow: "hidden",
            backgroundColor: "#fff",
            boxShadow:
              "0px 2px 1px -1px rgb(0 0 0 / 20%), 0px 1px 1px 0px rgb(0 0 0 / 14%), 0px 1px 3px 0px rgb(0 0 0 / 12%)",
          }}
        >
          <Box
            style={{
              display: "flex",
              alignItems: "center",
              padding: "8px 12px",
              borderBottom: "1px solid #f0f0f0",
            }}
          >
            <Typography style={{ fontSize: "12px", color: "#666" }}>
              ✕
            </Typography>
            <Box style={{ flex: 1, textAlign: "center" }}>
              <Typography
                style={{ fontSize: "12px", fontWeight: 600, color: "#111" }}
              >
                {websiteUrl
                  ? websiteUrl.replace(/^https?:\/\//, "").split("/")[0]
                  : "yourwebsite.com"}
              </Typography>
              <Typography style={{ fontSize: "10px", color: "#999" }}>
                Facebook
              </Typography>
            </Box>
            <Typography style={{ fontSize: "12px", color: "#666" }}>
              ⋯
            </Typography>
          </Box>
          <Box
            style={{
              height: "80px",
              backgroundColor: "#fafafa",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
            }}
          >
            <Typography style={{ fontSize: "11px", color: "#c0c0c0" }}>
              Your website
            </Typography>
          </Box>
          <Box
            style={{
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              gap: "8px",
              padding: "10px 12px",
              borderTop: "1px solid #f0f0f0",
            }}
          >
            <Box
              style={{
                display: "flex",
                alignItems: "center",
                gap: "8px",
                minWidth: 0,
              }}
            >
              <Box
                style={{
                  position: "relative",
                  width: "28px",
                  height: "28px",
                  borderRadius: "50%",
                  overflow: "hidden",
                  backgroundColor: "#e8f5ee",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  fontSize: "12px",
                  color: "#109d59",
                  fontWeight: 600,
                  flexShrink: 0,
                }}
              >
                {(pageName || "P").charAt(0).toUpperCase()}
                {picture && (
                  <img
                    src={picture}
                    alt=""
                    onError={(e) => {
                      e.currentTarget.style.display = "none";
                    }}
                    style={{
                      position: "absolute",
                      inset: 0,
                      width: "100%",
                      height: "100%",
                      objectFit: "cover",
                    }}
                  />
                )}
              </Box>
              <Box style={{ minWidth: 0 }}>
                <Typography
                  style={{
                    fontSize: "12px",
                    fontWeight: 600,
                    color: "#111",
                    lineHeight: 1.3,
                    whiteSpace: "nowrap",
                    overflow: "hidden",
                    textOverflow: "ellipsis",
                  }}
                >
                  {pageName || "Your Page"}
                </Typography>
                {whatsappNumber && (
                  <Typography
                    style={{
                      fontSize: "11px",
                      color: "#888",
                      lineHeight: 1.3,
                      whiteSpace: "nowrap",
                    }}
                  >
                    {whatsappNumber.startsWith("+")
                      ? whatsappNumber
                      : `+${whatsappNumber}`}
                  </Typography>
                )}
              </Box>
            </Box>
            <Box
              style={{
                display: "flex",
                alignItems: "center",
                gap: "5px",
                backgroundColor: "#128C7E",
                borderRadius: "18px",
                padding: "6px 12px",
                flexShrink: 0,
              }}
            >
              <WhatsAppIcon style={{ color: "#fff", fontSize: "15px" }} />
              <Typography
                style={{ fontSize: "12px", fontWeight: 600, color: "#fff" }}
              >
                WhatsApp
              </Typography>
            </Box>
          </Box>
        </Box>
      </Box>
    )}
    </Box>
  );
}

const styles = (theme) => ({
  editableCta: {
    cursor: "pointer",
    borderRadius: "6px",
    transition: "background-color 120ms ease",
    "&:hover": { backgroundColor: "rgba(0,0,0,0.04)" },
  },
  editableText: {
    outline: "none",
    cursor: "text",
  },

  root: {
    height: "100%",
    boxShadow:
      "0px 2px 1px -1px rgb(0 0 0 / 20%), 0px 1px 1px 0px rgb(0 0 0 / 14%), 0px 1px 3px 0px rgb(0 0 0 / 12%)",
  },

  media: {
    maxHeight: 600,
    "object-fit": "contain",
  },

  // Carousel-only: Facebook's actual carousel card crops each card's media
  // into a fixed-height box regardless of the source image/video's own
  // aspect ratio — a tall portrait upload must not stretch the card height.
  // Single-format media keeps the original `media` behavior above.
  carouselMedia: {
    width: "100%",
    height: CAROUSEL_MEDIA_SIZE,
    objectFit: "cover",
    display: "block",
    backgroundColor: "#000",
  },

  // WhatsApp Status / Instagram Story preview: full-height vertical (9:16)
  // dark frame with the media filling it edge to edge.
  statusCard: {
    position: "relative",
    // 9:16 is the real Status surface ratio; keep the width small enough
    // that the total height (~480px) matches the feed-card preview's
    // footprint, so switching ad types doesn't jump the preview column.
    width: "270px",
    aspectRatio: "9 / 16",
    borderRadius: "12px",
    overflow: "hidden",
    backgroundColor: "#000",
    boxShadow:
      "0px 2px 1px -1px rgb(0 0 0 / 20%), 0px 1px 1px 0px rgb(0 0 0 / 14%), 0px 1px 3px 0px rgb(0 0 0 / 12%)",
  },
  statusMedia: {
    position: "absolute",
    inset: 0,
    width: "100%",
    height: "100%",
    objectFit: "cover",
  },
});

export default withStyles(styles)(AdView);
